import { create } from 'zustand';
import { menuService } from '@/services/menuService';
import { showToast } from '@/lib/toast';
import {
    MenuWithDetails,
    UpdateMenuTreeDto,
    AvailableCategory,
    AvailablePage
} from '@/types/menu';

interface MenuState {
    // Menu data
    menu: MenuWithDetails | null;
    isLoading: boolean;
    isUpdating: boolean;
    error: string | null;

    // Available items for selection
    availableCategories: AvailableCategory[];
    availablePages: AvailablePage[];
    categoryChildren: AvailableCategory[];
    isLoadingCategories: boolean;
    isLoadingPages: boolean;
    isLoadingChildren: boolean;

    // Actions
    fetchMenu: () => Promise<void>;
    updateMenu: (updateMenuDto: UpdateMenuTreeDto) => Promise<void>;
    fetchAvailableCategories: (search?: string) => Promise<void>;
    fetchAvailablePages: (search?: string) => Promise<void>;
    fetchCategoryChildren: (categoryId: string, search?: string) => Promise<void>;
    clearError: () => void;
    resetMenu: () => void;
}

export const useMenuStore = create<MenuState>((set) => ({
    // Initial state
    menu: null,
    isLoading: false,
    isUpdating: false,
    error: null,
    availableCategories: [],
    availablePages: [],
    categoryChildren: [],
    isLoadingCategories: false,
    isLoadingPages: false,
    isLoadingChildren: false,

    // Actions
    fetchMenu: async () => {
        set({ isLoading: true, error: null });
        try {
            const menu = await menuService.getMenuTree();
            console.log("menu: ", menu)
            set({ menu, isLoading: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Lỗi khi tải menu';
            set({
                error: errorMessage,
                isLoading: false
            });
            showToast.error("Lỗi", errorMessage);
        }
    },

    updateMenu: async (updateMenuDto: UpdateMenuTreeDto) => {
        set({ isUpdating: true, error: null });
        try {
            const updatedMenu = await menuService.updateMenu(updateMenuDto);
            set({ menu: updatedMenu, isUpdating: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Lỗi khi cập nhật menu';
            set({
                error: errorMessage,
                isUpdating: false
            });
            showToast.error("Lỗi", errorMessage);
            throw error;
        }
    },

    fetchAvailableCategories: async (search?: string) => {
        set({ isLoadingCategories: true, error: null });
        try {
            const categories = await menuService.getAvailableCategories(search);
            set({ availableCategories: categories, isLoadingCategories: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Lỗi khi tải danh sách category';
            set({
                error: errorMessage,
                isLoadingCategories: false
            });
            showToast.error("Lỗi", errorMessage);
        }
    },

    fetchAvailablePages: async (search?: string) => {
        set({ isLoadingPages: true, error: null });
        try {
            const pages = await menuService.getAvailablePages(search);
            set({ availablePages: pages, isLoadingPages: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Lỗi khi tải danh sách page';
            set({
                error: errorMessage,
                isLoadingPages: false
            });
            showToast.error("Lỗi", errorMessage);
        }
    },

    fetchCategoryChildren: async (categoryId: string, search?: string) => {
        set({ isLoadingChildren: true, error: null });
        try {
            const children = await menuService.getCategoryChildren(categoryId, search);
            set({ categoryChildren: children, isLoadingChildren: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Lỗi khi tải danh sách category con';
            set({
                error: errorMessage,
                isLoadingChildren: false
            });
            showToast.error("Lỗi", errorMessage);
        }
    },

    clearError: () => set({ error: null }),
    resetMenu: () => set({
        menu: null,
        availableCategories: [],
        availablePages: [],
        categoryChildren: [],
        error: null
    }),
}));
